// API keys tab — keys for machines that call docs (`/api/v1/api-keys`).
// Create names the key, picks the access it holds (one of docs' presets, from
// the scope catalogue docs-api serves) and an optional end date; the secret
// is shown once with a copy affordance. Every row shows what an admin
// auditing access wants to know: which preset (or custom scope set) the key
// holds, when it ends and when it last authenticated a request. Revoking
// confirms first — a revoked key cannot be brought back.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Copy, Trash2, Loader2, Plus, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, ep } from '../lib/api.js';
import { presetLabel, presetMatching, useScopeCatalogue } from '../lib/scopeCatalogue.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';

const NAME_MAX = 255;

const STATUS_TONE = {
  active: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  expired: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  revoked: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
};

function fmt(value) {
  return value ? moment(value).format('DD MMM YYYY HH:mm') : '—';
}

function statusOf(key) {
  if (key.revoked_at) return 'revoked';
  if (key.expires_at && moment(key.expires_at).isBefore(moment())) return 'expired';
  return 'active';
}

function accessLabel(key, presets, t) {
  const preset = presetMatching(key.scopes, presets);
  if (preset) return presetLabel(t, preset);
  return t('apiKeys.presets.custom', { count: (key.scopes || []).length });
}

export const ApiKeysTab = () => {
  // Strings shared with the `apiKey` namespace are reused from it
  // (`apiKey:` / `common:` prefixes) instead of being duplicated here.
  const { t } = useTranslation('console');
  const { catalogue } = useScopeCatalogue();
  const presets = useMemo(() => catalogue?.presets || {}, [catalogue]);
  const presetNames = Object.keys(presets);
  const [keys, setKeys] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('');
  const [expires, setExpires] = useState('');
  const [createdSecret, setCreatedSecret] = useState(null);
  const [pendingRevoke, setPendingRevoke] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get(ep.apiKeys());
    if (res.success) {
      setKeys(Array.isArray(res.response) ? res.response : []);
      setLoadError(null);
    } else {
      setLoadError(res.message);
      setKeys((prev) => prev || []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The least the catalogue offers is the default: its first preset.
  const chosenPreset = preset || presetNames[0] || '';

  const openCreate = () => {
    setName('');
    setPreset('');
    setExpires('');
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error(t('apiKeys.toasts.nameRequired'));
      return;
    }
    if (!chosenPreset) return;
    setCreating(true);
    const data = { name: name.trim(), scopes: presets[chosenPreset] || [] };
    // The key works through the end of the chosen day, in the admin's time.
    if (expires) data.expires_at = moment(expires, 'YYYY-MM-DD').endOf('day').toISOString();
    const { success, response, message } = await api.post(ep.apiKeys(), { data });
    setCreating(false);
    if (!success) {
      toast.error(message);
      return;
    }
    setCreateOpen(false);
    setCreatedSecret(response);
    toast.success(t('apiKeys.toasts.created'));
    load();
  };

  const handleRevoke = async (key) => {
    const { success, message } = await api.del(ep.apiKey(key.uuid));
    if (success) {
      toast.success(t('apiKeys.toasts.revoked'));
      load();
    } else {
      toast.error(message);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(t('apiKeys.toasts.copied'));
  };

  const query = search.trim().toLowerCase();
  const shown = (keys || []).filter(
    (k) => !query || k.name.toLowerCase().includes(query) || (k.key_prefix || '').toLowerCase().includes(query),
  );
  const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 min-w-56 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('apiKeys.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        <Button
          onClick={openCreate}
          className="flex items-center gap-2 text-white font-medium rounded-lg px-[21px] py-[12px] h-auto cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }}
        >
          <KeyRound className="w-4 h-4" />
          {t('apiKey:apiKeysTab.createButton')}
        </Button>
      </div>

      {loadError && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{loadError}</p>}

      {keys === null ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" />
        </div>
      ) : shown.length > 0 ? (
        <div className="w-full border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('apiKeys.table.name')}</TableHead>
                <TableHead>{t('apiKeys.table.keyPrefix')}</TableHead>
                <TableHead>{t('apiKeys.table.preset')}</TableHead>
                <TableHead className="w-28">{t('apiKeys.table.status')}</TableHead>
                <TableHead className="text-center">{t('apiKeys.table.expires')}</TableHead>
                <TableHead className="text-center">{t('apiKeys.table.created')}</TableHead>
                <TableHead className="text-center">{t('apiKeys.table.lastUsed')}</TableHead>
                <TableHead className="text-center">{t('apiKey:apiKeysTab.table.action')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((key) => {
                const status = statusOf(key);
                return (
                  <TableRow key={key.uuid} data-row-id={key.uuid}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-muted-foreground">{key.key_prefix}…</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copy(key.key_prefix)} aria-label={t('apiKeys.copyPrefix')}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-xs text-foreground"
                        title={(key.scopes || []).join(', ')}
                      >
                        {accessLabel(key, presets, t)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${STATUS_TONE[status]}`}>
                        {t(`apiKeys.status.${status}`)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {key.expires_at ? fmt(key.expires_at) : t('apiKeys.neverExpires')}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{fmt(key.created_at)}</TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground" title={key.last_used_at ? moment(key.last_used_at).fromNow() : undefined}>
                      {key.last_used_at ? fmt(key.last_used_at) : t('apiKeys.neverUsed')}
                    </TableCell>
                    <TableCell className="text-center">
                      {status !== 'revoked' && (
                        <Button size="sm" variant="destructive" onClick={() => setPendingRevoke(key)} className="h-8" aria-label={t('apiKeys.revoke.title')}>
                          <Trash2 className="h-4 w-4" color="#fff" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <KeyRound className="h-16 w-16 mb-4 text-muted-foreground/60" />
          <p className="text-lg font-medium">{query ? t('list.noMatch') : t('apiKey:apiKeysTab.empty.title')}</p>
          <p className="text-sm text-muted-foreground">{query ? t('list.noMatchHint') : t('apiKeys.empty.description')}</p>
          {query && <Button variant="ghost" size="sm" className="mt-2" onClick={() => setSearch('')}>{t('list.clearFilters')}</Button>}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('apiKey:apiKeysTab.createButton')}</DialogTitle>
            <DialogDescription>{t('apiKeys.create.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="api-key-name">{t('apiKeys.create.nameLabel')}</Label>
              <Input
                id="api-key-name"
                placeholder={t('apiKeys.create.namePlaceholder')}
                value={name}
                maxLength={NAME_MAX}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-key-preset">{t('apiKeys.create.presetLabel')}</Label>
              <Select value={chosenPreset} onValueChange={setPreset} disabled={presetNames.length === 0}>
                <SelectTrigger id="api-key-preset" className="w-full" aria-label={t('apiKeys.create.presetLabel')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presetNames.map((p) => (
                    <SelectItem key={p} value={p} title={presets[p].join(', ')}>
                      {presetLabel(t, p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {chosenPreset && (
                <p className="text-xs text-muted-foreground font-mono break-words" data-testid="api-key-scopes">
                  {(presets[chosenPreset] || []).join(', ')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-key-expires">{t('apiKeys.create.expiresLabel')}</Label>
              <Input id="api-key-expires" type="date" min={tomorrow} value={expires} onChange={(e) => setExpires(e.target.value)} />
              <p className="text-xs text-muted-foreground">{t('apiKeys.create.expiresHint')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={creating || !name.trim() || !chosenPreset} data-testid="api-key-create">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('apiKeys.create.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret shown once */}
      <Dialog open={!!createdSecret} onOpenChange={(open) => !open && setCreatedSecret(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('apiKeys.secret.title')}</DialogTitle>
            <DialogDescription>{t('apiKeys.secret.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 bg-muted/50 border rounded-lg p-3">
            <code className="font-mono text-sm break-all flex-1" data-testid="api-key-secret">
              {createdSecret?.plain_key}
            </code>
            <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(createdSecret?.plain_key)} aria-label={t('apiKeys.secret.copy')}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedSecret(null)}>{t('apiKeys.secret.done')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingRevoke)}
        onOpenChange={(o) => { if (!o) setPendingRevoke(null); }}
        title={t('apiKeys.revoke.title')}
        description={t('apiKeys.revoke.body', { name: pendingRevoke?.name ?? '' })}
        confirmLabel={t('apiKeys.revoke.confirm')}
        destructive
        onConfirm={() => handleRevoke(pendingRevoke)}
      />
    </>
  );
};
